import { useContext, useEffect, useState } from 'react'
import { useQuery, useMutation, useLazyQuery } from '@apollo/react-hooks'
import { useHistory } from 'react-router-dom'
import COLLECTION_QUERY from '../graphql/collection.gql'
import ADD_SELECTIONS_MUTATION from '../graphql/add-on-boarding-selections.gql'
import ON_BOARDING_STATUS from '../graphql/on-boarding-status.gql'
import ADD_TO_MY_LIST_MUTATION from '../../../../graphql/mutations/add-to-my-list.gql'
import MY_LIST_QUERY from '../../../../graphql/queries/my-list.gql'
import ACCOUNT_QUERY_WITH_PROFILES from '../../../../graphql/queries/account-with-profiles.gql'
import { useIsAuthenticated } from '../../../hooks/use-is-authenticated'
import {
  UseItemAnimationContext,
  FADE_OUT,
  LOADING
} from './use-item-animation'

const collectionId = 'c02cdef3-2167-4aa3-ad56-07cb61ccb33d'

/**
 * Handle logic to display content selections, and after selecting
 */
export const useContentSelection = () => {
  const [, dispatch] = useContext(UseItemAnimationContext)
  // collection tiles
  const [collection, setCollection] = useState([])
  // State for checking if waiting for recommendation result
  const [isPending, setIsPending] = useState(false)
  const history = useHistory()
  const isAuthenticated = useIsAuthenticated()

  const {
    data: collectionData,
    loading: collectionLoading,
    error: collectionError
  } = useQuery(COLLECTION_QUERY, {
    variables: {
      collectionId
    }
  })

  useEffect(() => {
    if (!collectionLoading && collectionData) {
      collectionData &&
        collectionData.collection &&
        collectionData.collection.tiles.length > 0 &&
        setCollection(collectionData.collection.tiles)
    }
  }, [collectionData, collectionLoading, collectionError])

  // Change animation loading state
  // it will check onBoardStatus within 5 sec
  // within 5 sec, once status meets the requirement, will direct to homepage
  // after 5 sec, will direct to homepage anyway
  useEffect(() => {
    if (isPending) {
      // 1 second delay for fading out selected items
      const timerForLoading = setTimeout(() => {
        dispatch({ type: LOADING, loading: true })

        let counter = 0
        const timer = setInterval(() => {
          counter += 1
          getBoardStatus()

          if (counter > 5) {
            clearInterval(timer)
            clearTimeout(timerForLoading)
            history.push('/')
          }
        }, 1000)
      }, 1000)
    }
  }, [isPending])

  const [
    getBoardStatus,
    {
      data: onBoardingStatus,
      loading: onBoardingLoading,
      error: onBoardingError
    }
  ] = useLazyQuery(ON_BOARDING_STATUS)

  useEffect(() => {
    if (onBoardingError) {
      history.push('/')
    }

    if (onBoardingStatus && !onBoardingLoading) {
      if (
        onBoardingStatus.onboardStatus.status === 'COMPLETED' ||
        onBoardingStatus.onboardStatus.status === 'ERROR'
      ) {
        history.push('/')
      }
    }
  }, [onBoardingStatus, onBoardingLoading, onBoardingError])

  const [addToList] = useMutation(ADD_TO_MY_LIST_MUTATION)
  const [addSelections] = useMutation(ADD_SELECTIONS_MUTATION)
  const [getAccount] = useLazyQuery(ACCOUNT_QUERY_WITH_PROFILES, {
    fetchPolicy: 'network-only'
  })

  /**
   * Handles the confirmation flow for onboarding selections:
   * Add selected contents to Onboarding Selections and My List.
   * Logs errors to the console but does not interrupt the overall process.
   * Redirects user to the home page after all mutations finish (based on isPending state).
   *
   * @param {string[]} selectedList - A list of content item id
   */
  const confirmHandler = async (selectedList) => {
    setIsPending(true)

    try {
      // Run ACCOUNT_QUERY to fetch the latest avatar before navigating home page
      if (isAuthenticated) {
        getAccount()
      }

      const addSelectionsPromise = addSelections({
        variables: { selections: selectedList }
      })

      // Run all addToList mutations in parallel
      const addToMyListPromises = selectedList.map(id => addToList({
        variables: { contentItemId: id },
        refetchQueries: [{ query: MY_LIST_QUERY }]
      }))

      const [addSelectionsResult, addToListResults] = await Promise.all([
        addSelectionsPromise,
        Promise.all(addToMyListPromises)
      ])

      const isSelectionsPending = addSelectionsResult.data?.addOnboardSelections?.isPending
      const allContentsAddedToMyList = addToListResults.every(
        (res) => res?.data?.addToMyList === true
      )

      if (isSelectionsPending) {
        console.error('addOnboardSelections mutations failed')
      }

      if (!allContentsAddedToMyList) {
        console.error('One or more addToMyList mutations failed')
      }
    } catch (e) {
      console.error(e)
    }

    setIsPending(false)
    dispatch({ type: FADE_OUT, fadeOut: true })
  }

  return {
    contents: collection,
    confirmHandler,
    loading: collectionLoading
  }
}
